import { supabase } from './supabase';
import { logger } from './logger';

async function logAudit(action: string, targetType: string, targetId: string, details?: any) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return;
  await supabase.from('growth_audit_log').insert({
    admin_id: session.user.id,
    action,
    target_type: targetType,
    target_id: targetId,
    details: details || {},
  });
}

export async function sendAdminNotification(userId: string, title: string, body: string): Promise<void> {
  logger.functional('send_admin_notification', 'notificationService', { userId, title });
  const { error } = await supabase.from('notifications').insert({
    user_id: userId,
    type: 'admin_message',
    title,
    body,
    data: { from: 'admin' },
  });
  if (error) {
    logger.error('send_admin_notification', 'notificationService', { userId, error: error.message });
    throw error;
  }
  logger.technical('send_admin_notification_ok', 'notificationService', { userId });
  await logAudit('send_admin_notification', 'notification', userId, { title });
}

export async function sendBulkNotification(userIds: string[], title: string, body: string): Promise<number> {
  logger.functional('send_bulk_notification', 'notificationService', { recipientCount: userIds.length, title });
  const rows = userIds.map((uid) => ({
    user_id: uid,
    type: 'admin_message' as const,
    title,
    body,
    data: { from: 'admin' },
  }));

  const { error, data } = await supabase.from('notifications').insert(rows).select('id');
  if (error) {
    logger.error('send_bulk_notification', 'notificationService', { error: error.message });
    throw error;
  }

  const count = data?.length || 0;
  await logAudit('send_bulk_notification', 'notifications', '', {
    count,
    title,
    userIds,
  });
  return count;
}

export interface AuditEntry {
  id: string;
  adminId: string;
  action: string;
  targetType: string;
  targetId: string;
  details: any;
  createdAt: string;
}

export async function fetchAuditLog(limit = 50): Promise<AuditEntry[]> {
  const { data, error } = await supabase
    .from('growth_audit_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    logger.error('fetch_audit_log', 'notificationService', { error: error.message });
    throw error;
  }
  logger.technical('fetch_audit_log_ok', 'notificationService', { count: (data || []).length });
  return (data || []).map((e: any) => ({
    id: e.id,
    adminId: e.admin_id,
    action: e.action,
    targetType: e.target_type || '',
    targetId: e.target_id || '',
    details: e.details || {},
    createdAt: new Date(e.created_at).toLocaleString('fr-FR'),
  }));
}
